import React from 'react';

import { Box, Tooltip, Typography } from '@mui/material';

import { FraudScoreDetails } from './FraudScoreDetails';
import { SingleOrderPayments } from './SingleOrderPayments';

interface Props {
  order: App.Order;
}

function SingleExperienceDetails({ order }: Props) {
  const formattedTotal = order.total.toLocaleString(undefined, { maximumFractionDigits: 0 });

  // Get non-complimentary experience items (complimentary ones are attached to accommodation)
  const standaloneExperienceItems = order.experience_items.filter((item) => !item.complimentary);
  const firstExperienceItem = standaloneExperienceItems[0];

  // Calculate lead days from order creation to experience date
  let leadDays = 999;
  const experienceDate = firstExperienceItem?.ticket?.date;
  if (experienceDate) {
    leadDays = Math.floor(
      (new Date(experienceDate).setHours(0, 0, 0, 0) - new Date(order.created_at).setHours(0, 0, 0, 0)) /
        (1000 * 60 * 60 * 24),
    );
  }

  // Get experience title(s)
  const experienceTitles = standaloneExperienceItems
    .map((item) => item.title)
    .filter(Boolean)
    .slice(0, 3) // Limit to first 3 for display
    .join(', ');

  const additionalCount = standaloneExperienceItems.length > 3 ? standaloneExperienceItems.length - 3 : 0;

  // Get traveller info from first experience item
  const travellerInfo = firstExperienceItem?.traveller_info?.customer;
  const travellerName = travellerInfo
    ? `${travellerInfo.firstName || ''} ${travellerInfo.lastName || ''}`.trim()
    : null;
  const travellerEmail = travellerInfo?.email;

  return (
    <Box>
      <a target="_blank" href={`/purchases/${order.id}`} rel="noreferrer">
        experience
      </a>{' '}
      for {order.currency_code}
      {formattedTotal}{' '}
      {order.fk_purchaser_id && order.fk_purchaser_id !== order.fk_customer_id && <strong>Spoofed Order</strong>}
      <br />
      <strong>Account:</strong> {order.customer_full_name} | {order.customer_email} | {order.customer_phone_prefix}
      {order.customer_phone}
      <br />
      {travellerName && (
        <>
          <strong>Traveller:</strong> {travellerName}
          {travellerEmail && travellerEmail !== order.customer_email && (
            <>
              {' '}
              | {travellerEmail}
              <Tooltip title="Traveller email is different from account email">
                <span> 🟠</span>
              </Tooltip>
            </>
          )}
          {travellerEmail && travellerEmail === order.customer_email && (
            <Tooltip title="Traveller email matches account email">
              <span> 🟢</span>
            </Tooltip>
          )}
          <br />
        </>
      )}
      <strong>Experience:</strong> {experienceTitles || 'Unknown experience'}
      {additionalCount > 0 && ` +${additionalCount} more`}
      <br />
      {leadDays}d lead time{' '}
      {leadDays <= 10 && (
        <Tooltip title="Fraudsters book quickly before we can catch them! Booking under 10 days in advance is a risk. Under 3 days is high risk.">
          <span>{leadDays <= 3 ? '🔴' : '🟠'}</span>
        </Tooltip>
      )}
      {leadDays > 10 && (
        <Tooltip title="Longer lead times are more likely to be genuine customers.">
          <span>🟢</span>
        </Tooltip>
      )}
      <br />
      <Typography component="span" sx={{ fontStyle: 'italic' }}>
        {standaloneExperienceItems.length} experience item{standaloneExperienceItems.length !== 1 ? 's' : ''}
      </Typography>{' '}
      <Tooltip title="Experience bookings are generally lower risk for fraud compared to hotels and flights.">
        <span>🟢</span>
      </Tooltip>
      <br />
      <strong>Provider:</strong> {firstExperienceItem?.provider || 'Unknown'}
      <br />
      <strong>Platform:</strong> {order.platform}
      <br />
      <FraudScoreDetails order={order} />
      <SingleOrderPayments order={order} />
    </Box>
  );
}

export { SingleExperienceDetails };
